import React, { useState, useEffect } from "react";
import {
  FaSearch,
  FaTimes,
  FaUserCircle,
  FaEye,
  FaEdit,
  FaCamera,
  FaChevronLeft, // Added for pagination
  FaChevronRight, // Added for pagination
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import SummaryApi from "../common";

const AllCustomers = () => {
  const [search, setSearch] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const navigate = useNavigate();

  // --- START PAGINATION ADDITION ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8; // Change this number to show more/less per page

  // Reset to first page when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);
  // --- END PAGINATION ADDITION ---

  // Change this in production or use environment variable

  const BACKEND_BASE_URL = SummaryApi?.productImage?.url
    ? SummaryApi.productImage.url.replace("/api/uploads", "")
    : "http://192.168.1.144:3001";

  useEffect(() => {
    fetchAllUsers();
  }, []);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [modalOpen]);

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(SummaryApi.allUser.url, {
        method: SummaryApi.allUser.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer oakyaecom_no_units_916",
        },
      });

      const data = await response.json();

      if (data?.status === true) {
        setAllUsers(data.data || []);
      } else {
        toast.error(data?.message || "Failed to load customers");
        setAllUsers([]);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Something went wrong. Please try again.");
      setAllUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = Array.isArray(allUsers)
    ? allUsers.filter((user) =>
        `${user?.user_name || ""} ${user?.email || ""} ${user?.mobile_number || ""}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      )
    : [];

  // --- START PAGINATION CALCULATION ---
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  // --- END PAGINATION CALCULATION ---

  const openEditModal = (user) => {
    setEditUser(user);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditUser(null);
  };

  const viewCustomerDashboard = (userId) => {
    const encodedId = btoa(userId.toString());
    navigate(`/admin-panel/personaldashboard/${encodedId}`);
  };

  const showSuccessToast = (title, description) => {
    toast.custom(
      () => (
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="max-w-md w-full bg-white shadow-2xl rounded-3xl border-b-4 border-pink-500 overflow-hidden pointer-events-auto"
        >
          <div className="flex p-5">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 rounded-2xl bg-pink-100 flex items-center justify-center text-pink-600">
                <CheckCircle size={28} />
              </div>
            </div>
            <div className="ml-4 flex-1">
              <p className="text-base font-bold text-gray-900">{title}</p>
              <p className="mt-1 text-sm text-gray-600">{description}</p>
            </div>
          </div>
        </motion.div>
      ),
      {
        id: "customer-success",
        duration: 500,
        position: "top-center",
      },
    );
  };

  // ── Edit Modal with Profile Image Upload ────────────────────────────────
  const EditCustomerModal = () => {
    const [formData, setFormData] = useState({
      user_name: editUser?.user_name || "",
      email: editUser?.email || "",
      mobile_number: editUser?.mobile_number || "",
      alternate_mobile_number: editUser?.alternate_mobile_number || "",
      gender: editUser?.gender || "",
      city: editUser?.city || "",
      address: editUser?.address || "",
      is_active: editUser?.is_active === 1,
    });

    const [profilePreview, setProfilePreview] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);

    // Set initial profile preview
    useEffect(() => {
      if (editUser?.profile_image) {
        setProfilePreview(`${BACKEND_BASE_URL}/${editUser.profile_image}`);
      } else {
        setProfilePreview(null);
      }
      setSelectedImage(null); // Reset selected file on modal open
    }, [editUser]);

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    };

    const handleImageChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setSelectedImage(file);
        setProfilePreview(URL.createObjectURL(file));
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();

      const formPayload = new FormData();

      formPayload.append("user_id", editUser.user_id);
      formPayload.append("user_name", formData.user_name);
      formPayload.append("email", formData.email);
      formPayload.append("mobile_number", formData.mobile_number);
      formPayload.append(
        "alternate_mobile_number",
        formData.alternate_mobile_number || "",
      );
      formPayload.append("gender", formData.gender || "");
      formPayload.append("city", formData.city || "");
      formPayload.append("address", formData.address || "");
      formPayload.append("is_active", formData.is_active ? "1" : "0");

      if (selectedImage) {
        formPayload.append("profile_image", selectedImage);
      }

      try {
        const res = await fetch(SummaryApi.updateUser.url, {
          method: SummaryApi.updateUser.method,
          headers: {
            Authorization: "Bearer oakyaecom_no_units_916",
          },
          body: formPayload,
        });

        const result = await res.json();

        if (result.status === true) {
          showSuccessToast(
            "Success!",
            `${formData.user_name} updated successfully`,
          );
          fetchAllUsers();
          closeModal();
        } else {
          toast.error(result.message || "Failed to update customer");
        }
      } catch (err) {
        console.error(err);
        toast.error("Network error. Please try again.");
      }
    };

    return (
      <div className="fixed inset-0 bg-black/65 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b z-10 px-6 py-4 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Edit Customer</h2>
            <button
              onClick={closeModal}
              className="p-2 rounded-full hover:bg-gray-100 transition"
            >
              <FaTimes className="text-2xl text-gray-600" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Profile Image Upload Section */}
            <div className="flex flex-col items-center gap-4 pb-4">
              <div className="relative">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-gray-200 shadow-md">
                  {profilePreview ? (
                    <img
                      src={profilePreview}
                      alt="Profile preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/150?text=Error";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <FaUserCircle className="text-6xl text-gray-400" />
                    </div>
                  )}
                </div>

                <label
                  htmlFor="profile-upload"
                  className="absolute bottom-0 right-0 bg-pink-600 text-white p-3 rounded-full cursor-pointer hover:bg-pink-700 shadow-lg transition transform hover:scale-110"
                >
                  <FaCamera className="text-lg" />
                  <input
                    id="profile-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                </label>
              </div>
              <p className="text-sm text-gray-500">
                Click camera to change profile photo
              </p>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  name="user_name"
                  value={formData.user_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  disabled
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleChange}
                  disabled
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alternate Mobile
                </label>
                <input
                  type="tel"
                  name="alternate_mobile_number"
                  value={formData.alternate_mobile_number}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition bg-white"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Address
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition resize-none"
              />
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center cursor-pointer gap-3 select-none">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div
                  className={`w-12 h-6 rounded-full transition-colors duration-200 ease-in-out ${formData.is_active ? "bg-pink-500" : "bg-gray-300"}`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200 ease-in-out ${formData.is_active ? "translate-x-6" : "translate-x-1"} mt-0.5`}
                  />
                </div>
                <span className="font-medium text-gray-700">
                  Active Account
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-4 pt-6 border-t">
              <button
                type="button"
                onClick={closeModal}
                className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-8 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold rounded-xl hover:from-pink-600 hover:to-rose-600 transition shadow-md"
              >
                Update Customer
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  };

  // ── Customer Card Component ────────────────────────────────────────────────
  const CustomerCard = ({ user }) => {
    const profileImage = user.profile_image
      ? `${BACKEND_BASE_URL}/${user.profile_image}`
      : null;

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -6, transition: { duration: 0.3 } }}
        className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 relative"
      >
        <button
          onClick={() => openEditModal(user)}
          className="absolute top-4 right-4 z-10 p-2.5 bg-white/90 backdrop-blur-sm rounded-full shadow-md hover:bg-pink-50 text-pink-600 transition-all"
          title="Edit Customer"
        >
          <FaEdit className="text-xl" />
        </button>

        <div className="relative h-64 bg-gradient-to-b from-pink-50 to-gray-50 overflow-hidden">
          {profileImage ? (
            <img
              src={profileImage}
              alt={user.user_name || "Customer"}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/400x300?text=No+Photo";
              }}
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <FaUserCircle className="text-9xl text-gray-300" />
            </div>
          )}

          <div className="absolute top-4 left-4">
            <span
              className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-wide ${
                user.is_active === 1
                  ? "bg-green-100 text-green-700 border border-green-200"
                  : "bg-red-100 text-red-700 border border-red-200"
              }`}
            >
              {user.is_active === 1 ? "ACTIVE" : "INACTIVE"}
            </span>
          </div>
        </div>

        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-3 truncate">
            {user.user_name || "Unnamed Customer"}
          </h3>

          <div className="space-y-2.5 text-sm text-gray-700">
            <p>
              <span className="font-semibold">Email:</span> {user.email || "N/A"}
            </p>
            <p>
              <span className="font-semibold">Mobile:</span>{" "}
              {user.mobile_number || "N/A"}
            </p>
            <p>
              <span className="font-semibold">City:</span> {user.city || "N/A"}
            </p>
            <p>
              <span className="font-semibold">Joined:</span>{" "}
              {formatDate(user.created_date)}
            </p>
          </div>

          <button
            onClick={() => viewCustomerDashboard(user.user_id)}
            className="mt-6 w-full py-3.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold rounded-2xl hover:from-pink-600 hover:to-rose-600 transition-all shadow-lg flex items-center justify-center gap-2.5 text-base"
          >
            <FaEye />
            View Dashboard
          </button>
        </div>
      </motion.div>
    );
  };

  const SkeletonCard = () => (
    <div className="bg-white rounded-3xl shadow-lg overflow-hidden animate-pulse">
      <div className="h-64 bg-gray-200" />
      <div className="p-6 space-y-4">
        <div className="h-7 bg-gray-200 rounded w-4/5" />
        <div className="space-y-3">
          <div className="h-5 bg-gray-200 rounded" />
          <div className="h-5 bg-gray-200 rounded w-5/6" />
          <div className="h-5 bg-gray-200 rounded w-3/4" />
        </div>
        <div className="h-14 bg-gray-200 rounded-2xl mt-6" />
      </div>
    </div>
  );

  return (
    <>
      <Toaster position="top-center" toastOptions={{ duration: 4000 }} />

      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-purple-50 pb-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6"
          >
            <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
              All Customers
            </h1>

            <div className="relative w-full md:w-96">
              <FaSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                placeholder="Search name, email, mobile..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="
                  w-full pl-14 pr-6 py-4
                  bg-white border border-gray-200
                  rounded-full shadow-md
                  focus:ring-4 focus:ring-pink-200 focus:border-pink-400
                  outline-none transition-all text-gray-800 placeholder-gray-500
                "
              />
            </div>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
              {[...Array(8)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : filteredUsers.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-32"
            >
              <FaUserCircle className="text-9xl text-gray-300 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-gray-700 mb-2">
                {search ? "No matching customers found" : "No customers yet"}
              </h2>
              <p className="text-gray-500 max-w-md mx-auto">
                {search
                  ? "Try different search terms"
                  : "New customers will appear here once they register"}
              </p>
            </motion.div>
          ) : (
            <>
              {/* Changed filteredUsers.map to currentItems.map */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
                {currentItems.map((user) => (
                  <CustomerCard key={user.user_id} user={user} />
                ))}
              </div>

              {/* Added Pagination Controls */}
              {totalPages > 1 && (
                <div className="mt-12 flex justify-center items-center gap-3">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="p-4 rounded-2xl bg-white border border-gray-200 text-gray-600 hover:bg-pink-50 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm"
                  >
                    <FaChevronLeft />
                  </button>

                  <div className="flex gap-2">
                    {[...Array(totalPages)].map((_, index) => {
                      const page = index + 1;
                      // Logic to show limited page numbers
                      if (totalPages > 5 && Math.abs(currentPage - page) > 1 && page !== 1 && page !== totalPages) {
                        if (page === 2 || page === totalPages - 1) return <span key={page} className="px-1 text-gray-400">...</span>;
                        return null;
                      }
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`w-12 h-12 rounded-2xl font-bold transition shadow-sm border ${
                            currentPage === page
                              ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white border-transparent"
                              : "bg-white text-gray-600 hover:border-pink-300 hover:bg-pink-50 border-gray-200"
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="p-4 rounded-2xl bg-white border border-gray-200 text-gray-600 hover:bg-pink-50 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm"
                  >
                    <FaChevronRight />
                  </button>
                </div>
              )}
              <p className="text-center mt-6 text-gray-500 font-medium">
                Showing {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredUsers.length)} of {filteredUsers.length} Customers
              </p>
            </>
          )}
        </div>

        {modalOpen && <EditCustomerModal />}
      </div>
    </>
  );
};

export default AllCustomers;